import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../lib/api';
import { useCurrency } from '../store/currency';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function ChartTooltip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--stone)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow)' }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => <div key={p.name} style={{ fontSize: 12.5, color: p.color }}>{p.name}: {fmt(p.value)}</div>)}
    </div>
  );
}

export default function ReportsPage() {
  const { fmt } = useCurrency();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try { setData(await api.getAnnualSummary(y)); }
    catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(year); }, [year]);

  const monthChart = data ? MONTHS.map((m, i) => {
    const d = data.by_month[String(i + 1)] || { income: 0, expenses: 0, net: 0 };
    return { name: m, Income: d.income, Expenses: d.expenses };
  }) : [];

  const catData = data ? Object.entries(data.by_category).slice(0, 12).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-eyebrow">Reports</div>
          <div className="page-title">Annual Summary</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Year-end overview and tax-ready export</div>
        </div>
        <div className="flex gap-3 items-center">
          <select className="input" style={{ width: 100 }} value={year} onChange={e => setYear(parseInt(e.target.value))}>
            {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y}>{y}</option>)}
          </select>
          {data && (
            <a
              href={api.exportCSV(year)}
              download={`ledger-${year}.csv`}
              className="btn btn-ghost"
            >
              ↓ Export CSV
            </a>
          )}
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60, color: 'var(--mid)' }}>Loading…</div>}

      {data && !loading && (
        <>
          {/* KPIs */}
          <div className="grid-4 mb-6">
            <div className="card">
              <div className="stat-label">Total Income</div>
              <div className="stat-value green">{fmt(data.total_income)}</div>
              <div className="stat-sub">{data.transaction_count} transactions</div>
            </div>
            <div className="card">
              <div className="stat-label">Total Expenses</div>
              <div className="stat-value red">{fmt(data.total_expenses)}</div>
            </div>
            <div className="card">
              <div className="stat-label">Net Saved</div>
              <div className={`stat-value ${data.net >= 0 ? 'green' : 'red'}`}>{fmt(data.net)}</div>
              <div className="stat-sub">{data.total_income > 0 ? ((data.net / data.total_income) * 100).toFixed(1) : 0}% savings rate</div>
            </div>
            <div className="card">
              <div className="stat-label">Net Invested</div>
              <div className="stat-value gold">{fmt(data.investments.net_invested)}</div>
              <div className="stat-sub">{data.investments.transaction_count} trades</div>
            </div>
          </div>

          {/* Month chart */}
          <div className="card mb-6">
            <div className="section-title mb-4">Income vs Expenses by Month</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthChart} barSize={14} barGap={4}>
                <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={(props) => <ChartTooltip {...props} fmt={fmt} />} />
                <Bar dataKey="Income" fill="var(--green-bg)" stroke="var(--green)" strokeWidth={1} radius={[3,3,0,0]} />
                <Bar dataKey="Expenses" fill="var(--red-bg)" stroke="var(--red)" strokeWidth={1} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid-2">
            {/* Spending by category */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--stone)' }}>
                <div className="section-title" style={{ marginBottom: 0 }}>Spending by Category</div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>% of Expenses</th>
                  </tr>
                </thead>
                <tbody>
                  {catData.map(({ name, value }) => (
                    <tr key={name}>
                      <td style={{ fontSize: 13 }}>{name}</td>
                      <td className="text-right amount-neg">{fmt(value)}</td>
                      <td className="text-right" style={{ color: 'var(--mid)', fontSize: 12 }}>
                        {data.total_expenses > 0 ? ((value / data.total_expenses) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Month-by-month table */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--stone)' }}>
                <div className="section-title" style={{ marginBottom: 0 }}>Monthly Breakdown</div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th style={{ textAlign: 'right' }}>Income</th>
                    <th style={{ textAlign: 'right' }}>Expenses</th>
                    <th style={{ textAlign: 'right' }}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map((m, i) => {
                    const d = data.by_month[String(i + 1)];
                    if (!d) return null;
                    return (
                      <tr key={m}>
                        <td style={{ fontWeight: 500 }}>{m}</td>
                        <td className="text-right amount-pos">{fmt(d.income)}</td>
                        <td className="text-right amount-neg">{fmt(d.expenses)}</td>
                        <td className="text-right" style={{ fontWeight: 500, color: d.net >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(d.net)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 60, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>≣</div>
          <div style={{ fontWeight: 500, marginBottom: 5 }}>No data for {year}</div>
          <div style={{ color: 'var(--mid)', fontSize: 13 }}>Upload transactions to see your annual summary</div>
        </div>
      )}
    </div>
  );
}
